import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";

export interface ZipFileSpec {
  name: string;
  bucketPath: string;
}

function uniqueName(name: string, used: Set<string>): string {
  if (!used.has(name)) {
    used.add(name);
    return name;
  }
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let i = 2;
  while (used.has(`${base} (${i})${ext}`)) i++;
  const out = `${base} (${i})${ext}`;
  used.add(out);
  return out;
}

export async function downloadAsZip(
  bucket: string,
  files: ZipFileSpec[],
  zipName: string
): Promise<void> {
  if (files.length === 0) return;

  const paths = files.map(f => f.bucketPath);
  const { data: signed, error } = await supabase.storage.from(bucket).createSignedUrls(paths, 3600);
  if (error || !signed) throw error ?? new Error("No se pudieron firmar las URLs");

  const zip = new JSZip();
  const used = new Set<string>();

  await Promise.all(
    signed.map(async (s, i) => {
      if (!s.signedUrl) return;
      const res = await fetch(s.signedUrl);
      const blob = await res.blob();
      zip.file(uniqueName(files[i].name, used), blob);
    })
  );

  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = zipName.endsWith(".zip") ? zipName : `${zipName}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadSingle(bucket: string, path: string, name: string): Promise<void> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600, {
    download: name,
  });
  if (error || !data) throw error ?? new Error("No se pudo generar URL");
  const a = document.createElement("a");
  a.href = data.signedUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
